// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Button, Box, Image, Grid, Text, Switch, Spinner, useDisclosure, Input, Textarea } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { IoLocationSharp, IoClose, IoPeople } from 'react-icons/io5';
import { TbRainbow } from 'react-icons/tb';
import { MdWork } from 'react-icons/md';
import { FaGenderless, FaRuler, FaAmericanSignLanguageInterpreting, FaReact } from 'react-icons/fa';
import { MdOutlineInterests } from 'react-icons/md';
import { TbEdit } from 'react-icons/tb';
import { FaCheck } from 'react-icons/fa6';
import { GiGemini, GiAries, GiTaurus, GiCancer, GiLeo, 
  GiVirgo, GiLibra, GiScorpio, GiSagittarius, GiCapricorn, 
  GiAquarius, GiPisces } from 'react-icons/gi';
import { SiGotomeeting } from 'react-icons/si';
import { MdEmojiPeople } from 'react-icons/md';
import { MdVerified } from 'react-icons/md';
import { useQuery } from 'react-query';
import { LiaBirthdayCakeSolid } from 'react-icons/lia';

// Assets
import { getAge, parseDateBirthday } from 'services/Utils';
import { textLang } from 'services/Lang';

// Components
import EditField from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';
import ErrorCard from 'components/Form/Errors';
import BirthdayField from 'components/Form/BirthdayField';

const ProfileDetails = ({
	object, 
	handleUpdateObject,
	handleField,
	toggleInterest
}) => {
	const textColor = '#0E1633';

	const [editMyCard, setEditMyCard] = useState(false);
	const interestsOptions = [
	  'Music', 'Sports', 'Travel', 'Movies', 'Gaming', 'Reading',
	  'Cooking', 'Dancing', 'Art', 'Technology', 'Fitness', 'Photography',
	  'Nature', 'Fashion', 'Anime',
	  'Hiking', 'Camping', 'Running', 'Swimming', 'Yoga', 'Meditation',
	  'Astronomy', 'Science', 'Psychology', 'Philosophy', 'History', 'Politics',
	  'Languages', 'Writing', 'Poetry', 'Painting', 'Drawing', 'DIY',
	  'Gardening', 'Cars', 'Motorcycles', 'Aviation', 'Space', 'Coding',
	  'Startups', 'Crypto', 'AI', 'Esports', 'Board Games', 'Chess',
	  'Volunteering', 'Animals', 'Dogs', 'Cats', 'Coffee', 'Tea',
	  'Food', 'Baking', 'Wine', 'Traveling', 'Beaches', 'Mountains',
	  'Urban Exploring', 'Minimalism', 'Sustainability', 'Interior Design', 'Makeup', 'Skincare',
	  'Tattoos', 'Piercing', 'Festivals', 'Photography Film', 'Vintage', 'Comics',
	  'Manga', 'KPop', 'Movies Classic', 'Standup', 'Podcasts', 'Collecting',
	  'Traveling Solo', 'Surfing', 'Snowboarding', 'Hiking Trails'
	];

	return (
	<Box bg="white" mt="16px" borderRadius="16px">
		<Flex alignItems="center" justifyContent="space-between" mb="12px">
			<Text variant="text-md" px="4px" w="80%">{textLang('about_me_header')}</Text>
			
			{ !editMyCard ?
					<TbEdit onClick={() => setEditMyCard(!editMyCard)} color={textColor} fontSize="36px" />
					:
					<Flex overflow="visible !important" gap="16px" alignItems="top">
						<Box mt="-2px">
							<IoClose color={textColor} onClick={() => {
								setEditMyCard(!editMyCard);
							}} fontSize="34px" /> 
						</Box>

						<Box overflow="visible !important">
							<FaCheck color={textColor} onClick={() => {
								setEditMyCard(!editMyCard);
								handleUpdateObject();
							}} fontSize="30px"/> 
						</Box>
					</Flex>
			}
		</Flex>

		{ !editMyCard ?
			<>
				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<IoPeople color={textColor} fontSize="21px" /> 
						<Text>{textLang(object?.gender)}</Text>
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<TbRainbow color={textColor} fontSize="21px" /> 
						{ object?.genderOfInterest ?
            	<Text>{textLang(object?.genderOfInterest)}</Text>
            	:
            	<Text opacity="0.6">{textLang('placeholder_gender_interest')}</Text>
            }
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<FaRuler color={textColor} fontSize="21px" /> 
						{ object?.size && object?.size !== 0 ? 
           		<Text>{object?.size}</Text>
           		:
           		<Text opacity="0.6">{textLang('placeholder_how_tall_are_you')}</Text>
           	}
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<MdEmojiPeople fontSize="21px" color={textColor} /> 
            { object?.relationship ? 
           		<Text>{textLang(object?.relationship)}</Text>
           		:
           		<Text opacity="0.6">{textLang('placeholder_relationship')}</Text>
           	}
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<MdWork fontSize="21px" color={textColor} /> 
            { object?.career ? 
           		<Text>{object?.career}</Text>
           		:
           		<Text opacity="0.6">{textLang('placeholder_career')}</Text>
           	}
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<FaReact fontSize="21px" color={textColor} /> 
            { object?.religion ? 
           		<Text>{object?.religion}</Text>
           		:
           		<Text opacity="0.6">{textLang('placeholder_religion')}</Text>
           	}
					</Flex>
				</Flex>

				<Flex justifyContent="space-between" py="12px" alignItems="center" borderBottom="1px solid #cccccc4a">
					<Flex alignItems="center" gap="8px">
						<IoLocationSharp color={textColor} fontSize="21px" />
            <Text>{object?.country}, {object?.city}</Text>
					</Flex>
				</Flex>
			</>
			:
			<>
			  <EditField mt="12px" mb="-12px" bg="#f1f1f1 !important" editMode={true} id="firstName" placeholder={textLang('first_name')} 
        	type="text" value={object?.firstName} isValid={true} onChange={handleField?.firstName} />

        <EditField mb="-12px" bg="#f1f1f1 !important" editMode={true} id="lastName" placeholder={textLang('last_name')} 
        	type="text" value={object?.lastName} isValid={true} onChange={handleField?.lastName} />

        <Box mb="12px" className="select-gray">
	        <BirthdayField
					  editMode={true}
					  id="birthday"
					  type="custom"
					  value={object?.birthday}
					  isValid={true}
					  bg="#f1f1f1 !important"
					  onChange={handleField.birthday}
					/>
				</Box>

        <Textarea 
						borderRadius="16px !important" 
						p="12px !important" 
						w="100%" 
						bg="#f1f1f1 !important"
						type="text" 
						placeholder={textLang('bio')}
						onChange={handleField.description}
						value={object?.description}/>

				<Text variant="text-md" mt="40px" px="4px" textAlign="center">{textLang('change_tags')}</Text>

				{/*<Flex flexWrap="wrap" gap="8px" mt="16px" justifyContent="center">
				  {interestsOptions.map((interest) => (
				    <Box
				      key={interest}
				      cursor="pointer"
				      px="12px"
				      py="6px"
				      borderRadius="16px"
				      border="1px solid"
				      bg={object?.interests?.includes(interest) ? '#ff4133' : 'transparent'}
				      color={object?.interests?.includes(interest) ? 'white' : 'black'}
				      fontWeight="500"
				      onClick={() => toggleInterest(interest)}
				      transition="0.2s ease-in-out"
				    >
				      {textLang(interest)}
				    </Box>
				  ))}
				</Flex>*/}

				<Box position="relative">
					<Box
						pointerEvents="none"
						w="100%" h="30px" position="absolute" top="0" left="0" bg="linear-gradient(180deg, white, transparent)" zIndex="9" />
					<Box overflow="scroll" pb="30px" height="38vh" border="1px solid white">
						<Flex flexWrap="wrap" gap="8px" mt="12px" justifyContent="center">
						  {interestsOptions.map((interest) => (
						    <Box
						      key={interest}
						      cursor="pointer"
						      px="12px"
						      py="6px"
						      borderRadius="16px"
						      border="1px solid"
						      bg={object?.interests?.includes(interest) ? '#ff4133' : 'transparent'}
						      color={object?.interests?.includes(interest) ? 'white' : 'black'}
						      fontWeight="500"
						      onClick={() => toggleInterest(interest)}
						      transition="0.2s ease-in-out"
						    >
						      {textLang(interest)}
						    </Box>
						  ))}
						</Flex>
					</Box>
					<Box
						pointerEvents="none" 
						w="100%" h="30px" position="absolute" bottom="0" left="0" bg="linear-gradient(0deg, white, transparent)" zIndex="9" />
				</Box>

				<Text variant="text-md" mt="40px" px="4px" mb="16px" textAlign="center">
					{textLang('change_details')}
				</Text>

				<EditSelect 
					mb="-12px"
        	editMode={true}
        	bg="#f1f1f1 !important"
        	options={[
        		{ value: 'Male', title: textLang('Male') },
        		{ value: 'Female', title: textLang('Female') },
        		{ value: 'Transgender', title: textLang('Transgender') },
        		{ value: 'Unknown', title: textLang('gender_other') },
        	]}
        	id="gender" value={object?.gender} onChange={handleField?.gender} isValid={true}  />

        <EditSelect 
          mb="-12px"
        	editMode={true}
        	bg="#f1f1f1 !important"
        	options={[
        		{ value: 'Male', title: textLang('Male') },
        		{ value: 'Female', title: textLang('Female') },
        		{ value: 'Transgender', title: textLang('Transgender') },
        		{ value: 'Unknown', title: textLang('gender_other') },
        	]}
        	id="genderOfInterest" placeholder={textLang('placeholder_gender_interest')}  value={object?.genderOfInterest} onChange={handleField?.genderOfInterest} isValid={true}  />

        <EditField mb="-12px" bg="#f1f1f1 !important" editMode={true} id="size" placeholder={textLang('placeholder_how_tall_are_you')} 
        	type="number" value={object?.size} isValid={true} onChange={handleField?.size} />

        <EditSelect 
        	mb="-12px"
        	editMode={true}
        	bg="#f1f1f1 !important"
        	options={[
        		{ value: 'Single', title: textLang('Single') },
        		{ value: 'In a relationship', title: textLang('In a relationship') },
        		{ value: 'Married', title: textLang('Married') },
        		{ value: 'It’s complicated', title: textLang('It’s complicated') },
        		{ value: 'Open relationship', title: textLang('Open Relationship') },
        		{ value: 'Divorced', title: textLang('Divorced') },
        		{ value: 'Widowed', title: textLang('Widowed') }
        	]}
        	id="relationship" placeholder={textLang('placeholder_relationship')}  value={object?.relationship} onChange={handleField?.relationship} isValid={true}  />

        <EditField mb="-12px" bg="#f1f1f1 !important" editMode={true} id="career" placeholder={textLang('placeholder_career')}
        	type="text" value={object?.career} isValid={true} onChange={handleField?.career} />

        <EditSelect 
        	mb="-12px"
        	editMode={true}
        	bg="#f1f1f1 !important"
        	options={[
        		{ value: 'Christianity', title: textLang('Christianity')},
        		{ value: 'Islam', title: textLang('Islam') },
        		{ value: 'Hinduism', title: textLang('Hinduism') },
        		{ value: 'Buddhism', title: textLang('Buddhism') },
        		{ value: 'Judaism', title: textLang('Judaism') },
        		{ value: 'Sikhism', title: textLang('Sikhism') },
        		{ value: 'Atheism', title: textLang('Atheism') },
        		{ value: 'Agnosticism', title: textLang('Agnosticism') },
        		{ value: 'Other', title: textLang('Other') },
        		{ value: 'Not specified', title: textLang('Not Specified') }
        	]}
        	id="religion" placeholder={textLang('placeholder_religion')}  value={object?.religion} onChange={handleField?.religion} isValid={true}  />
      
        {/*<EditField mb="-12px" bg="#f1f1f1 !important" editMode={true} id="country" placeholder="Country" type="text" value={object?.country
		        } isValid={true} onChange={handleField?.country} />
		    
		    <EditField mb="-12px" bg="#f1f1f1 !important" editMode={true} id="city" placeholder="City" type="text" value={object?.city
		        } isValid={true} onChange={handleField?.city} />*/}
      </>
    }

	</Box>
	);
};

export default ProfileDetails;